import React from "react";
import { Link } from "react-router-dom";
import {
  HandsPraying,
  Heart,
  UsersThree,
  Sun,
  Leaf,
  Bandaids,
  Sparkle,
  ArrowRight,
} from "@phosphor-icons/react";
import { IMAGES } from "../lib/site";
import SectionHeading from "../components/SectionHeading";
import Seo from "../components/Seo";

const areas = [
  {
    icon: HandsPraying,
    title: "Grief Support",
    text: "Personalized encouragement for the loss of a spouse, parent, child, friend, or pregnancy. Grief has no timeline — and you don't walk it alone.",
  },
  {
    icon: Sun,
    title: "Spiritual Encouragement",
    text: "Chaplain-led prayer, devotional resources, and faith-rooted conversations for seasons of weariness, doubt, or transition.",
  },
  {
    icon: UsersThree,
    title: "Caregiver Support",
    text: "Compassionate care for those caring for aging parents, ill spouses, or hurting loved ones. You are not invisible.",
  },
  {
    icon: Heart,
    title: "Emotional Support",
    text: "A gentle, non-clinical space for sorrow, anxiety, loneliness, and discouragement — grounded in faith and active listening.",
  },
  {
    icon: Sparkle,
    title: "Prayer Support",
    text: "Confidential prayer requests cared for by chaplains and prayer partners committed to lifting you up.",
  },
  {
    icon: Bandaids,
    title: "Illness & Crisis Support",
    text: "Spiritual care companionship for diagnosis, treatment, hospitalization, hospice, and the long road of healing.",
  },
  {
    icon: Leaf,
    title: "Encouragement After Loss",
    text: "Resources and slow conversations for rebuilding your sense of self, faith, and future after profound loss.",
  },
];

const GriefSupport = () => {
  return (
    <div data-testid="page-grief-support">
      <Seo
        title="Grief & Bereavement Support"
        description="Compassionate, faith-based grief and bereavement support for individuals, families, and caregivers walking through the loss of a loved one. You are not alone."
      />
      <section className="py-20 sm:py-28">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Grief & spiritual support
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900">
              For the soul that is weary, tender, or quietly undone.
            </h1>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed max-w-2xl">
              Grief, illness, and overwhelming seasons aren't problems to be
              solved — they're sacred ground. Our chaplain-led care offers
              gentle presence, prayer, and practical encouragement, exactly
              when and how you need it.
            </p>
            <Link
              to="/request-support"
              data-testid="grief-request-btn"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              Request Support <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
          <div className="lg:col-span-5 reveal">
            <img
              src={IMAGES.conversation}
              alt="Two women in a supportive conversation"
              className="rounded-2xl shadow-xl border border-ink-100"
            />
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-softblue/40">
        <div className="container-x">
          <SectionHeading
            overline="Areas of care"
            title="Care that meets you in your specific season."
            subtitle="Each path of support is led by chaplain-trained encouragers who listen first and walk slowly."
            align="center"
            className="mb-16"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {areas.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`grief-area-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white rounded-2xl p-8 border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_18px_50px_-10px_rgba(15,23,42,0.15)] transition-all duration-300"
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-softblue text-primary">
                  <Icon size={24} weight="duotone" />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-medium text-ink-900">{title}</h3>
                <p className="mt-3 text-ink-700 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-x">
          <div className="rounded-3xl bg-paper border border-ink-100 p-10 sm:p-16 text-center">
            <p className="font-heading italic text-2xl sm:text-3xl text-ink-800 leading-relaxed max-w-3xl mx-auto">
              "The Lord is near to the brokenhearted, and saves the crushed in spirit."
            </p>
            <p className="mt-4 text-sm text-primary tracking-[0.22em] uppercase font-semibold">
              Psalm 34:18
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/request-support"
                data-testid="grief-cta-request-btn"
                className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                Request Encouragement
              </Link>
              <Link
                to="/classes"
                data-testid="grief-cta-classes-btn"
                className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-7 py-3.5 font-semibold hover:bg-primary/5 transition-all duration-300"
              >
                Join a Grief Workshop
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GriefSupport;
